/**
 * Persisted Apollo cache (PWA Phase 2 — offline access).
 *
 * The dashboard's Apollo cache is normally in-memory only. To let operators
 * open the app and see previously-loaded data with no / flaky connectivity we
 * persist the cache to IndexedDB (via localforage) and restore it on boot.
 *
 * MULTI-TENANCY (critical): persisted data is tenant-scoped, so it must never
 * leak across users or tenants. Two safeguards enforce this:
 *   1. The storage key embeds `<userId>::<tenantId>` — a different identity
 *      reads a different key and can never see another scope's data.
 *   2. On setup we purge every *other* scope, and on logout / tenant switch /
 *      user change callers invoke `purgeApolloPersistedCache()` to wipe it all.
 */
import type { InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { CachePersistor, LocalForageWrapper } from 'apollo3-cache-persist';
import localforage from 'localforage';
import log from 'loglevel';

const KEY_PREFIX = 'revvly-apollo-cache';

const store = localforage.createInstance({
  name: 'revvly-pwa',
  storeName: 'apollo-cache',
  description: 'Persisted Apollo cache for offline access',
});

let persistor: CachePersistor<NormalizedCacheObject> | null = null;

/**
 * Build the scope key from a verified identity, or null when either the
 * authenticated user or the selected tenant is missing (e.g. login screens).
 * A null scope means we deliberately persist nothing.
 *
 * The `userId` MUST be the live authenticated user's id (not the stored
 * `lastLoggedInUserId`, which can still hold the previous session's user until
 * TenantContext's change detector runs). Keying off the live identity ensures
 * we never restore a prior user's cache into a new user's session.
 */
function buildScopeKey(userId: string | null | undefined, tenantId: string | null): string | null {
  if (!userId || !tenantId) {
    return null;
  }
  return `${KEY_PREFIX}::${userId}::${tenantId}`;
}

async function purgeOtherScopes(keep: string): Promise<void> {
  try {
    const keys = await store.keys();
    await Promise.all(
      keys
        .filter((k) => k.startsWith(KEY_PREFIX) && k !== keep)
        .map((k) => store.removeItem(k)),
    );
  } catch (err) {
    log.warn('Failed to purge stale Apollo cache scopes:', err);
  }
}

/**
 * Restore the persisted cache for the current identity and begin persisting
 * subsequent writes. Safe to call when unauthenticated — it becomes a no-op.
 * Resolves once the cache has been restored (or immediately when skipped) so
 * the caller can render with warm data.
 */
export async function setupCachePersistence(
  cache: InMemoryCache,
  userId: string | null | undefined,
  tenantId: string | null,
): Promise<void> {
  const key = buildScopeKey(userId, tenantId);
  if (!key) {
    return;
  }

  try {
    // Tear down any prior persistor (e.g. identity changed within the session)
    // before wiring up the new scope.
    if (persistor) {
      await persistor.pause();
      persistor = null;
    }
    await purgeOtherScopes(key);

    persistor = new CachePersistor({
      cache,
      // IndexedDB — Apollo caches routinely exceed the localStorage quota.
      storage: new LocalForageWrapper(store),
      key,
      // Rely on scope-keying + explicit purge for eviction rather than a size
      // cap that would silently wipe the whole cache when exceeded.
      maxSize: false,
      trigger: 'write',
    });

    await persistor.restore();
    log.info('💾 Apollo cache restored from persistent storage');
  } catch (err) {
    log.warn('Failed to set up Apollo cache persistence:', err);
    persistor = null;
  }
}

/**
 * Wipe ALL persisted Apollo cache. Call on logout, tenant switch, and user
 * change so tenant-scoped data never survives an identity change.
 */
export async function purgeApolloPersistedCache(): Promise<void> {
  try {
    if (persistor) {
      await persistor.pause();
      await persistor.purge();
    }
    const keys = await store.keys();
    await Promise.all(
      keys.filter((k) => k.startsWith(KEY_PREFIX)).map((k) => store.removeItem(k)),
    );
    log.info('💾 Persisted Apollo cache purged');
  } catch (err) {
    log.warn('Failed to purge persisted Apollo cache:', err);
  }
}
